import React, { useState } from 'react'
import { StyleSheet, View, ViewProps } from 'react-native'
import { Color, Font, FormField, Icon, RadioGroup, RadioGroupProps, Spacing, Text } from 'zens-storybook'
import { Trans, useTranslation } from 'react-i18next'
import { PermissionContext, RolePermissions, RoleType, AccessType, useProfile } from 'zens-store'
import { titleForRole } from '@dms/helper'

const styles = StyleSheet.create({
  container: {
    marginBottom: Spacing.L,
    marginHorizontal: -Spacing.XS,
  },
  head: {
    flex: 1,
    flexDirection: 'row',
    marginBottom: Spacing.S,
  },
  row: {
    flex: 1,
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderColor: Color.sleepingGrey,
    minHeight: 42,
  },
  lastRow: {
    borderBottomWidth: 0,
  },
  group: {
    flex: 3,
    marginVertical: -Spacing.XS,
    flexDirection: 'row',
  },
  col: {
    flex: 1,
    marginVertical: Spacing.XS,
    marginLeft: Spacing.XS,
    marginRight: Spacing.XS,
  },
  access: {
    flex: 1,
    alignSelf: 'flex-end',
    marginHorizontal: Spacing.XS,
    fontSize: 14,
    fontFamily: Font.medium,
    color: Color.metalGrey,
  },
  cell: {
    borderRightWidth: 1,
    borderColor: Color.sleepingGrey,
    alignItems: 'center',
    justifyContent: 'center',
  },
  info: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  permission: {
    fontSize: 14,
    color: Color.metalGrey,
    marginLeft: Spacing.XS,
  },
  skeletonText: {
    flex: 1,
  },
  skeletonIcon: {
    minWidth: 40,
  },
})

const rolePages = ['insights', 'workspace', 'product', 'company', 'user', 'settings']

interface Props extends Omit<RadioGroupProps<number>, 'items' | 'error'> {
  companyId: number
  roleId?: RoleType
  error?: string
}
const UserRoleSelect = (props: Props) => {
  const { companyId, roleId, error, ...otherProps } = props
  const [hovered, setHovered] = useState<RoleType[]>([])
  const { t } = useTranslation()
  const profile = useProfile()
  const role = profile.data?.roleForCompany(companyId)

  const roles = Object.entries(RoleType)
    .filter((entry) => Number.isInteger(entry[1]))
    .map((entry) => entry[1])
    .filter((role) => role !== RoleType.SystemAdministrator) as RoleType[]

  function roleIsAllowed(value: RoleType) {
    if (role === RoleType.SystemAdministrator) return true
    if (role === RoleType.CompanyAdministrator) return true
    if (role === RoleType.Operator)
      return (
        (value === RoleType.Operator || value === RoleType.External) &&
        roleId !== RoleType.CompanyAdministrator &&
        roleId !== RoleType.Facilitator
      )
    if (role === RoleType.Facilitator)
      return (
        (value === RoleType.Facilitator || value === RoleType.External || value === RoleType.Viewer) &&
        roleId !== RoleType.CompanyAdministrator &&
        roleId !== RoleType.Operator
      )
    return role === value
  }

  const items = roles?.map((value) => ({
    value,
    title: titleForRole(value, t),
    onHoverIn: () => setHovered((oldValue) => [...oldValue, value]),
    onHoverOut: () => setHovered((oldValue) => oldValue.filter((item) => item !== value)),
    disabled: !roleIsAllowed(value),
  }))

  function titleForPermission(context: PermissionContext) {
    switch (context) {
      case 'insights':
        return t('Insights')
      case 'workspace':
        return t('Workspaces')
      case 'product':
        return t('Devices')
      case 'company':
        return t('Company')
      case 'user':
        return t('Users')
      case 'settings':
        return t('Settings')
    }
  }

  function iconForPermission(context: PermissionContext) {
    switch (context) {
      case 'insights':
        return <Icon name="graph" color={Color.metalGrey} />
      case 'workspace':
        return <Icon name="desk" color={Color.metalGrey} />
      case 'product':
        return <Icon name="signal" color={Color.metalGrey} />
      case 'company':
        return <Icon name="building" color={Color.metalGrey} />
      case 'user':
        return <Icon name="account" color={Color.metalGrey} />
      case 'settings':
        return <Icon name="gear" color={Color.metalGrey} />
    }
  }

  function renderRow(context: PermissionContext, index: number) {
    return (
      <View style={[styles.row, index === rolePages.length - 1 && styles.lastRow]} key={`permission-${context}`}>
        <View style={styles.group}>
          {roles?.map((role) => {
            const isSelected = props.value === role
            const isHovered = hovered.indexOf(role) >= 0
            return (
              <View style={[styles.col, styles.cell]} key={`role-${role}`}>
                {RolePermissions[context](role) === AccessType.Read ? (
                  <Text
                    style={{ color: isSelected ? Color.greyBlue : isHovered ? Color.premiumBlack : Color.calmGrey }}
                  >
                    <Trans>View only</Trans>
                  </Text>
                ) : RolePermissions[context](role) > 0 ? (
                  <Icon
                    name={isSelected ? 'checkmark' : 'checkmarkLight'}
                    color={isSelected ? Color.greyBlue : isHovered ? Color.premiumBlack : Color.calmGrey}
                  />
                ) : undefined}
              </View>
            )
          })}
        </View>
        <View style={[styles.col, styles.info]}>
          {iconForPermission(context)}
          <Text style={styles.permission}>{titleForPermission(context)}</Text>
        </View>
      </View>
    )
  }

  return (
    <FormField
      style={styles.container}
      label={t('Role')}
      help={t('The role a user has in the application')}
      error={error}
    >
      <View style={styles.head}>
        <RadioGroup items={items} style={styles.group} optionStyle={styles.col} error={!!error} {...otherProps} />
        <Text style={styles.access}>
          <Trans>Access to:</Trans>
        </Text>
      </View>
      {rolePages.map((pages, index) => renderRow(pages as PermissionContext, index))}
    </FormField>
  )
}

interface SkeletonProps extends ViewProps {}
const Skeleton = (props: SkeletonProps) => {
  const { style, ...otherProps } = props
  const { t } = useTranslation()

  function renderRow(key: string, index: number) {
    return (
      <View style={[styles.row, index === rolePages.length - 1 && styles.lastRow]} key={`permission-${key}`}>
        <View style={styles.group}>
          {Array.from({ length: 3 }).map((_, index) => (
            <View style={[styles.col, styles.cell]} key={`role-${index}`}>
              <Text.Skeleton level={1} style={styles.skeletonIcon} />
            </View>
          ))}
        </View>
        <View style={[styles.col, styles.info]}>
          <Text.Skeleton style={styles.skeletonText} />
        </View>
      </View>
    )
  }
  return (
    <FormField.Skeleton style={[styles.container, style]} label={t('Role')} {...otherProps}>
      <View style={styles.head}>
        <RadioGroup.Skeleton numberOfOptions={3} style={styles.group} optionStyle={styles.col} />
        <Text.Skeleton style={styles.access} />
      </View>
      {rolePages.map(renderRow)}
    </FormField.Skeleton>
  )
}
UserRoleSelect.Skeleton = Skeleton

export default UserRoleSelect
